import { arrayMoveImmutable } from 'array-move';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CreationService from 'services/CreationService';

const initialState = {
  busy: false,
  menuOpen: false,
  activeModal: null,
  loadError: false,
  showPlansModal: false,
  activeModalData: null,
  creation: null,
  activeSlide: null,
  snackbar: {
    open: false,
    severity: undefined,
    activeMessage: {
      text: undefined,
      severity: undefined,
    },
    nextMessage: {
      text: undefined,
      severity: undefined,
    },
  },
  isSlideLoaded: false,
};

export const generalSlice = createSlice({
  name: 'general',
  initialState: { ...initialState },
  reducers: {
    openComparisonModal: (state, action) => {
      state.showPlansModal = action.payload;
    },
    reset: () => {
      return { ...initialState };
    },
    setBusy: (state) => {
      state.busy = true;
    },
    setNotBusy: (state) => {
      state.busy = false;
    },
    openMenu: (state) => {
      state.menuOpen = true;
    },
    closeMenu: (state) => {
      state.menuOpen = false;
    },
    openModal: (state, action) => {
      state.activeModal = action.payload;
    },
    setModalData: (state, action) => {
      state.activeModalData = action.payload;
    },
    closeModal: (state) => {
      state.activeModal = null;
    },
    setLoadError: (state) => {
      state.loadError = true;
    },
    showSnackbar: (state, action) => {
      state.snackbar.nextMessage = action.payload;
    },
    openSnackbar: (state) => {
      state.snackbar.open = true;
      state.snackbar.activeMessage = state.snackbar.nextMessage;
      state.snackbar.nextMessage = {
        text: undefined,
        severity: undefined,
      };
    },
    closeSnackbar: (state) => {
      state.snackbar.open = false;
    },
    cleanSnackbar: (state) => {
      state.snackbar.activeMessage = {
        text: undefined,
        severity: undefined,
      };
    },
    creationLoaded: (state, action) => {
      state.creation = action.payload;
      state.loadError = false;
    },
    creationRenamed: (state, action) => {
      state.creation.name = action.payload;
    },
    updateSlides: (state, action) => {
      state.creation.slides = action.payload;
    },
    updateActiveSlide: (state, action) => {
      state.activeSlide = action.payload;
    },
    addCreationSlide: (state, action) => {
      state.creation.slides = [...state.creation.slides, action.payload];
    },
    removeCreationSlide: (state, action) => {
      const remainingSlides = state.creation.slides.filter(
        (slide) => slide.id !== action.payload.id,
      );
      const orderedSlides = remainingSlides.map((slide, index) => {
        slide.sequence = index + 1;

        return slide;
      });

      state.creation.slides = orderedSlides;
    },
    setSlideLoaded: (state, action) => {
      state.isSlideLoaded = action.payload;
    },
  },
});

export const {
  reset,
  setBusy,
  setNotBusy,
  openMenu,
  closeMenu,
  openModal,
  closeModal,
  setModalData,
  setLoadError,
  showSnackbar,
  openSnackbar,
  closeSnackbar,
  cleanSnackbar,
  creationLoaded,
  creationRenamed,
  updateSlides,
  updateActiveSlide,
  addCreationSlide,
  removeCreationSlide,
  openComparisonModal,
  setSlideLoaded,
} = generalSlice.actions;

export const fetchCreation = createAsyncThunk(
  'creation/fetch',
  async (data, { dispatch, getState }) => {
    const attemptFetch = async (retryCount = 0) => {
      try {
        const currentCreation = getState().general.creation;
        if (currentCreation && currentCreation.id !== parseInt(data.id)) {
          dispatch({ type: 'general/reset' });
        }

        const { data: creation } = await CreationService.findOne(data.id);

        dispatch(creationLoaded(creation));

        return creation;
      } catch (e) {
        console.error(e);
        dispatch(setLoadError());

        if (e.response && e.response.status === 403) {
          dispatch(
            showSnackbar({
              text: 'You do not have permission to view this creation',
              severity: 'load_error',
            }),
          );
        } else {
          if (retryCount < 4) {
            dispatch(
              showSnackbar({
                text: `Failed to fetch creation, retrying...`,
                severity: 'load_error',
              }),
            );

            // Exponential backoff for retries: 1s, 2s, 4s, 8s
            await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retryCount) * 1000));

            return attemptFetch(retryCount + 1);
          } else {
            console.error('Final retry to fetch creation failed.', e);
            dispatch(showSnackbar({ text: 'Failed to fetch creation', severity: 'load_error' }));
          }
        }
      }
    };

    return attemptFetch();
  },
);

export const renameCreation = createAsyncThunk(
  'creation/rename',
  async (data, { getState, dispatch }) => {
    const state = getState().general;

    try {
      dispatch(setBusy());
      dispatch(showSnackbar({ text: 'Renaming creation... Please wait', severity: 'processing' }));

      const { data: creation } = await CreationService.update(state.creation.id, {
        name: data.name,
      });

      dispatch(creationRenamed(creation.name));
      dispatch(showSnackbar({ text: 'Creation renamed successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to rename creation', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export const updateSlidesSequences = createAsyncThunk(
  'creation/updateSlidesSequences',
  async (data, { getState, dispatch }) => {
    const state = getState().general;

    try {
      dispatch(setBusy());
      dispatch(
        showSnackbar({ text: 'Updating slide sequence... Please wait', severity: 'processing' }),
      );

      const payload = {
        slides: arrayMoveImmutable(state.creation.slides, data.oldIndex, data.newIndex).map(
          (slide, index) => ({ id: slide.id, sequence: index + 1 }),
        ),
      };
      const { data: slides } = await CreationService.updateSlidesSequences(
        state.creation.id,
        payload,
      );

      dispatch(updateSlides(slides));
      dispatch(showSnackbar({ text: 'Slide sequence updated successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to update slide sequence', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export const addSlide = createAsyncThunk(
  'creation/addSlide',
  async (data, { getState, dispatch }) => {
    const state = getState().general;

    try {
      dispatch(setBusy());
      dispatch(showSnackbar({ text: 'Adding slide... Please wait', severity: 'processing' }));

      const { data: slide } = await CreationService.createSlide(state.creation.id, {
        cleanCreation: true,
      });

      dispatch(addCreationSlide(slide));
      dispatch(showSnackbar({ text: 'Slide added successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to add slide', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export const copySlide = createAsyncThunk(
  'creation/copySlide',
  async (data, { getState, dispatch }) => {
    const state = getState().general;

    try {
      dispatch(setBusy());
      dispatch(showSnackbar({ text: 'Copying slide... Please wait', severity: 'processing' }));

      await CreationService.copySlide(state.creation.id, state.activeSlide.id, {
        creationId: data.creationId,
      });

      dispatch(showSnackbar({ text: 'Slide copied successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to copy slide', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export const duplicateSlide = createAsyncThunk(
  'creation/duplicateSlide',
  async (data, { getState, dispatch }) => {
    const state = getState().general;

    try {
      dispatch(setBusy());
      dispatch(showSnackbar({ text: 'Duplicating slide... Please wait', severity: 'processing' }));

      const { data: slide } = await CreationService.duplicateSlide(
        state.creation.id,
        state.activeSlide.id,
      );

      dispatch(addCreationSlide(slide));
      dispatch(showSnackbar({ text: 'Slide duplicated successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to duplicate slide', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export const deleteSlide = createAsyncThunk(
  'creation/deleteSlide',
  async (data, { getState, dispatch }) => {
    const state = getState().general;
    const nextSlide = state.creation.slides.find((slide) => slide.id !== state.activeSlide.id);

    try {
      dispatch(setBusy());
      dispatch(showSnackbar({ text: 'Removing slide... Please wait', severity: 'processing' }));

      const { data: slide } = await CreationService.deleteSlide(
        state.creation.id,
        state.activeSlide.id,
      );

      dispatch(removeCreationSlide(slide));
      dispatch(updateActiveSlide(nextSlide));
      dispatch(showSnackbar({ text: 'Slide removed successfully', severity: 'success' }));
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ text: 'Failed to remove slide', severity: 'error' }));
    } finally {
      dispatch(setNotBusy());
    }
  },
);

export default generalSlice.reducer;
